import { sdk } from '../sdk';

export interface Transaction {
  _id: string;
  transactionId: string;
  accountId: {
    _id: string;
    accountNumber: string;
    name: string;
  };
  accountNumber: string;
  accountName: string;
  type: 'credit' | 'debit';
  amount: number;
  description: string;
  referenceType?: 'donation' | 'expense' | 'manual';
  referenceId?: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  createdByName: string;
  createdByRole: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTransactionData {
  accountId: string;
  type: 'credit' | 'debit';
  amount: number;
  description: string;
  referenceType?: 'donation' | 'expense' | 'manual';
  referenceId?: string;
}

export interface UpdateTransactionData {
  description?: string;
  amount?: number;
  type?: 'credit' | 'debit';
}

export interface TransactionSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  accountId?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

export const TransactionsAPI = {
  // Get all transactions with pagination and filters
  async getAllTransactions(params: TransactionSearchParams = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    // Default limit to 50 if not specified
    queryParams.append('limit', (params.limit || 50).toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.accountId) queryParams.append('accountId', params.accountId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (typeof params.minAmount === 'number') queryParams.append('minAmount', String(params.minAmount));
    if (typeof params.maxAmount === 'number') queryParams.append('maxAmount', String(params.maxAmount));

    const response = await sdk.client.get(`/api/v1/transactions?${queryParams.toString()}`);
    return response.data || response;
  },

  // Get recent transactions (excluding cash account)
  async getRecentTransactions(limit: number = 50): Promise<Transaction[]> {
    const response = await sdk.client.get(`/api/v1/transactions/recent?limit=${limit}`);
    // Handle both wrapped and unwrapped responses
    if (response.success && response.data && Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response)) {
      return response;
    }
    return [];
  },

  // Get transaction by ID
  async getTransactionById(id: string): Promise<Transaction> {
    const response = await sdk.client.get(`/api/v1/transactions/${id}`);
    return response.data || response;
  },

  // Create new transaction
  async createTransaction(data: CreateTransactionData): Promise<Transaction> {
    const response = await sdk.client.post('/api/v1/transactions', data);
    return response.data || response;
  },

  // Update transaction
  async updateTransaction(id: string, data: UpdateTransactionData): Promise<Transaction> {
    const response = await sdk.client.put(`/api/v1/transactions/${id}`, data);
    return response.data || response;
  },

  // Delete transaction
  async deleteTransaction(id: string): Promise<{ success: boolean; message: string }> {
    const response = await sdk.client.delete(`/api/v1/transactions/${id}`);
    return response.data || response;
  },

  // Get transaction statistics
  async getTransactionStats(params: { startDate?: string; endDate?: string; accountId?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.accountId) queryParams.append('accountId', params.accountId);

    const response = await sdk.client.get(`/api/v1/transactions/stats?${queryParams.toString()}`);
    return response.data || response;
  }
};
